import { forwardRef, useImperativeHandle, useRef, type ReactNode } from 'react';
import { ShakeContainer, BurstLayer, burst, type ShakeHandle } from '../../fx/index.js';
import { Spotlight, type SpotlightHandle } from './Spotlight.js';
import { centerInDesign, centerInViewport } from './coords.js';

/**
 * StageRevealShell — the reusable plumbing every reveal screen (Vote, Dawn, End…)
 * needs: a ShakeContainer wrapping the content, a screen-local BurstLayer for
 * impact particles, a Spotlight iris overlay, and a measured design-space root so
 * the two coordinate conversions (Spotlight ↔ burst) are handled in one place.
 *
 * A reveal screen renders its content as children and drives the choreography via
 * the handle (forwarded ref): on a sequencer cue it calls
 *   ref.spotlightOn(el)   — iris onto a DOM element (snap or ease)
 *   ref.burstAt(el, opts) — fire a particle burst at a DOM element
 *   ref.shake(intensity)  — jolt the frame
 * passing the element it captured via its own bust refs. The shell does the
 * getBoundingClientRect → design-px / viewport-fraction math internally.
 *
 * ── Two patterns worth remembering ────────────────────────────────────────────
 * 1. SPOTLIGHT-AFTER-LAYOUT: the iris needs the target's ON-SCREEN rect, so it can
 *    only aim once the busts have laid out. Always drive spotlightOn() from a
 *    sequencer onCue (e.g. the `spotlight` beat), which fires after mount — never
 *    from render. Aiming during the first render reads a zero/omitted rect.
 * 2. COINCIDENT BEATS ARE UNORDERED: if two sequence beats share the same ms (e.g.
 *    LYNCH_REVEAL `spotlight` and `name` both at 4500), they fire on the same tick
 *    and their relative order is just the object-key order in visualTuning.json —
 *    do NOT rely on one landing strictly before the other. If ordering matters,
 *    give them distinct ms.
 *
 * Reduced-motion: the shell forwards it to Spotlight (instant) and ShakeContainer
 * (shake → flash); the CALLER still suppresses its own burst calls under reduced
 * motion (bursts are decorative and the shell can't know intent).
 */

export interface StageRevealHandle {
  /** Iris the spotlight onto a DOM element. `mode` snap (fast) or ease (slow). */
  spotlightOn: (el: HTMLElement | null | undefined, opts?: { r?: number; mode?: 'snap' | 'ease' }) => void;
  /** Open the iris wide (clear focus). */
  spotlightClear: () => void;
  /** Fire a particle burst centered on a DOM element. */
  burstAt: (el: HTMLElement | null | undefined, opts: { preset: Parameters<typeof burst>[0]['preset']; count?: number }) => void;
  /** Jolt the frame (0..1). Reduced-motion degrades to a flash automatically. */
  shake: (intensity?: number) => void;
}

export interface StageRevealShellProps {
  children?: ReactNode;
  /** Spotlight darkness outside the iris (0 = no spotlight overlay). Default 0.72. */
  spotlightDark?: number;
  /** Optional spotlight cone art (screen-blend). */
  spotlightConeSrc?: string;
  className?: string;
  style?: React.CSSProperties;
}

export const StageRevealShell = forwardRef<StageRevealHandle, StageRevealShellProps>(
  function StageRevealShell({ children, spotlightDark = 0.72, className, style }, ref) {
    const shakeRef = useRef<ShakeHandle>(null);
    const spotRef = useRef<SpotlightHandle>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        spotlightOn: (el, opts) => {
          const target = centerInDesign(rootRef.current, el);
          if (!target) return;
          const t = { ...target, r: opts?.r ?? 200 };
          if (opts?.mode === 'ease') spotRef.current?.ease(t);
          else spotRef.current?.snap(t);
        },
        spotlightClear: () => spotRef.current?.clear(),
        burstAt: (el, opts) => {
          const { x, y } = centerInViewport(el);
          burst({ preset: opts.preset, x, y, count: opts.count });
        },
        shake: (intensity) => shakeRef.current?.shake(intensity),
      }),
      [],
    );

    return (
      <ShakeContainer ref={shakeRef} className={className} style={{ position: 'absolute', inset: 0, ...style }}>
        <BurstLayer />
        <div ref={rootRef} style={{ position: 'absolute', inset: 0 }}>
          {children}
        </div>
        <Spotlight ref={spotRef} dark={spotlightDark} />
      </ShakeContainer>
    );
  },
);
